import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Heading from 'LuxKit/Typography/Heading'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'
import LineGlobeIcon from 'LuxKit/Icons/line/LineGlobeIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import Group from 'components/utils/Group'
import { CruiseContentSections, CruiseSectionTabValues } from 'constants/cruise'
import {
  getCruiseCountriesByItinerary,
  getCruisePortsByItinerary,
} from 'lib/cruises/cruiseUtils'
import OfferDetailImageGallery from '../OfferDetail/OfferDetailImageGallery'
import OfferDetailDescription from '../OfferDetail/OfferDetailDescription'
import CruiseContentTabs from 'components/Cruises/CruiseOfferPage/content/CruiseContentTabs'
import CruiseOfferPageItinerary from 'components/Cruises/CruiseOfferPage/content/CruiseItinerary/CruiseOfferPageItinerary'
import CruiseShipOverview from 'components/Cruises/CruiseOfferPage/content/CruiseShip/CruiseShipOverview'
import CruiseShipAmenities from 'components/Cruises/CruiseOfferPage/content/CruiseShip/CruiseShipAmenities/CruiseShipAmenities'
import CruiseDecksAndCabins from 'components/Cruises/CruiseOfferPage/content/DecksAndCabins/CruiseDecksAndCabins'
import CruiseFinePrint from 'components/Cruises/CruiseOfferPage/content/FinePrint/CruiseFinePrint'
import CruiseFAQ from 'components/Cruises/CruiseOfferPage/content/FAQ/CruiseFAQ'
import useCruiseContentSectionLabels from 'hooks/Cruise/useCruiseContentSectionLabels'
import { DRAWER_DETAIL_CONTENT_WIDTH } from '../constants'

const ContentWrapper = styled.div`
  margin: 0 auto;
  padding: ${rem(24)};
  width: 100%;
  max-width: ${rem(DRAWER_DETAIL_CONTENT_WIDTH)};
  min-width: 0;
  box-sizing: border-box;
`

const MetaGrid = styled.div`
  display: grid;
  gap: ${rem(8)};
  grid-template-columns: repeat(3, minmax(0, 1fr));
`

const TabContent = styled.div`
  padding-top: ${rem(24)};
`

const Section = styled(Group)`
  min-width: 0;
`

type DrawerCruiseTab = Exclude<
  CruiseSectionTabValues,
  CruiseSectionTabValues.DATES_PRICING
>

type SectionTabs = {
  label: string
  id: DrawerCruiseTab
}

function getInitialTab(tabs: Array<SectionTabs>): DrawerCruiseTab {
  return tabs[0]?.id ?? CruiseSectionTabValues.ABOUT_SHIP
}

interface Props {
  offer: App.CruiseOffer
  showTitleInContent?: boolean
}

function CruiseOfferDetailView({ offer, showTitleInContent = false }: Props) {
  const intl = useIntl()
  const sectionLabels = useCruiseContentSectionLabels()
  const [currentTab, setCurrentTab] = useState<DrawerCruiseTab>(() =>
    offer.itinerary?.length
      ? CruiseSectionTabValues.ITINERARY
      : CruiseSectionTabValues.ABOUT_SHIP,
  )

  const ports = useMemo(
    () => getCruisePortsByItinerary(offer.itinerary),
    [offer.itinerary],
  )
  const countries = useMemo(
    () => getCruiseCountriesByItinerary(offer.itinerary),
    [offer.itinerary],
  )
  const departurePortCity = useMemo(
    () => offer.departurePort?.split(',')?.[0],
    [offer.departurePort],
  )
  const returnPortCity = useMemo(
    () => offer.returnPort?.split(',')?.[0],
    [offer.returnPort],
  )
  const hasSameStartAndEndPort = offer.departurePort === offer.returnPort
  const summaryDescription = offer.ship.shipInfo?.find(
    (info) => info.dataTypeName === 'Summary',
  )?.description
  const offerDescription = offer.description || summaryDescription || offer.destinationDescription
  const hasDecksAndCabins = !!(
    offer.ship?.cabinCategories?.length && offer.ship?.decks?.length
  )
  const hasFAQ = !!(
    offer.ship.shipInfo?.length && offer.cruiseLine.vendorInfo?.length
  )

  const tabs = useMemo<Array<SectionTabs>>(() => {
    const nextTabs: Array<SectionTabs> = []
    if (offer.itinerary?.length) {
      nextTabs.push({
        label: intl.formatMessage({
          id: 'eUwovuumZR8iNkTz7vTp',
          defaultMessage: 'Itinerary',
          description:
            'Tab label for itinerary section on the itinerary or schedule view',
        }),
        id: CruiseSectionTabValues.ITINERARY,
      })
    }
    nextTabs.push(
      {
        label: intl.formatMessage({
          id: '2mNbeTs8gDqJkL9e4e6X',
          defaultMessage: 'Ship & Cabins',
          description:
            'Tab label for about the ship section in the cruises section',
        }),
        id: CruiseSectionTabValues.ABOUT_SHIP,
      },
      {
        label: intl.formatMessage({
          id: 'I1xB1k-Ue6Yu2HIlhhUY',
          defaultMessage: 'Fine Print',
          description:
            'Tab label for fine print section in the cruises section',
        }),
        id: CruiseSectionTabValues.FINE_PRINT,
      },
    )
    if (hasFAQ) {
      nextTabs.push({
        label: intl.formatMessage({
          id: '7FUPVopNIRxB5VZWkOEK',
          defaultMessage: 'FAQs',
          description:
            'Navigation tab label for the frequently asked questions section on the offer page',
        }),
        id: CruiseSectionTabValues.FAQ,
      })
    }
    return nextTabs
  }, [hasFAQ, intl, offer.itinerary?.length])

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === currentTab)) {
      setCurrentTab(getInitialTab(tabs))
    }
  }, [currentTab, tabs])

  const hasItinerary = !!offer.itinerary?.length

  return (
    <ContentWrapper>
      <Group direction="vertical" gap={24}>
        {offer.images.length > 0 && (
          <OfferDetailImageGallery images={offer.images} />
        )}

        {showTitleInContent && (
          <Heading variant="heading3">
            {offer.name}
          </Heading>
        )}

        <Section direction="vertical" gap={4}>
          <BodyText variant="medium" colour="neutral-two">
            {hasSameStartAndEndPort && (
              <FormattedMessage
                id="4hYAGlgNNjRcOyR8ABSu"
                defaultMessage="Starts and ends in <b>{portCity}</b>"
                description="Port information when cruise starts and ends in same city"
                values={{
                  portCity: returnPortCity,
                  b: (chunks: ReactNode) => <b>{chunks}</b>,
                }}
              />
            )}
            {!hasSameStartAndEndPort && (
              <FormattedMessage
                id="dcz1lmt8ZdCHFNGcP6y5"
                defaultMessage="Starts in <b>{departureCity}</b>, ends in <b>{returnCity}</b>"
                description="Port information when cruise starts and ends in different cities"
                values={{
                  departureCity: departurePortCity,
                  returnCity: returnPortCity,
                  b: (chunks: ReactNode) => <b>{chunks}</b>,
                }}
              />
            )}
          </BodyText>
        </Section>

        <Group direction="vertical" gap={4}>
          <BodyText variant="medium" colour="neutral-two">
            <FormattedMessage
              id="jXo5KEojgJHWwKD9Biho"
              defaultMessage="Cruise line: <b>{operatorName}</b>"
              description="Cruise line label with operator name in the cruise offer or booking section"
              values={{
                operatorName: offer.cruiseLine.name,
                b: (chunks: ReactNode) => <b>{chunks}</b>,
              }}
            />
          </BodyText>
          <BodyText variant="medium" colour="neutral-two">
            <FormattedMessage
              id="5GtGndMbb0vOloVTwOJF"
              defaultMessage="Cruise ship: <b>{shipName}</b>"
              description="Cruise ship label with ship name in the cruise offer or booking section"
              values={{
                shipName: offer.ship.name,
                b: (chunks: ReactNode) => <b>{chunks}</b>,
              }}
            />
          </BodyText>
        </Group>

        <MetaGrid>
          <BodyTextBlock variant="medium" startIcon={<LineClockIcon />}>
            {intl.formatMessage(
              {
                id: 'CLy6gZpbx6rczN1kqb2H',
                defaultMessage:
                  '{count, plural, one {# night} other {# nights}}',
                description:
                  'Pluralised count of nights for the duration of a cruise itinerary listing',
              },
              { count: offer.duration },
            )}
          </BodyTextBlock>
          {ports.length > 0 && (
            <BodyTextBlock variant="medium" startIcon={<LineMapIcon />}>
              {intl.formatMessage(
                {
                  id: 'uLRiwPOxWXbbQ_N6N4e8',
                  defaultMessage:
                    '{count, plural, one {# port} other {# ports}}',
                  description:
                    'Number of ports visited on cruise itinerary in the cruise section',
                },
                { count: ports.length },
              )}
            </BodyTextBlock>
          )}
          {countries.length > 0 && (
            <BodyTextBlock variant="medium" startIcon={<LineGlobeIcon />}>
              {intl.formatMessage(
                {
                  id: 'LQprTJXP8GRDyA1AspwP',
                  defaultMessage:
                    '{count, plural, one {# country} other {# countries}}',
                  description:
                    'Number of countries visited on cruise itinerary in the cruises section',
                },
                { count: countries.length },
              )}
            </BodyTextBlock>
          )}
        </MetaGrid>

        {offerDescription && (
          <OfferDetailDescription description={offerDescription} />
        )}

        <Divider kind="secondary" />

        <CruiseContentTabs
          tabs={tabs}
          activeTab={currentTab}
          onTabsChange={(tab) => setCurrentTab(tab as DrawerCruiseTab)}
        />

        <TabContent>
          {currentTab === CruiseSectionTabValues.ITINERARY && hasItinerary && (
            <CruiseOfferPageItinerary
              offerId={offer.id}
              itinerary={offer.itinerary}
              cruiseType={offer.cruiseType}
              offerFallbackImageId={offer.heroImageId ?? offer.images[0]?.id}
              experienceCarouselPageSize={1}
            />
          )}

          {currentTab === CruiseSectionTabValues.ABOUT_SHIP && (
            <Group direction="vertical" gap={32}>
              <CruiseShipOverview offer={offer} showImages={false} />
              <CruiseShipAmenities ship={offer.ship} />
              {hasDecksAndCabins && (
                <Group direction="vertical" gap={24}>
                  <Heading variant="heading3">
                    {sectionLabels[CruiseContentSections.DECKS_AND_CABINS]}
                  </Heading>
                  <CruiseDecksAndCabins
                    ship={offer.ship}
                    hasGotToOverview={false}
                    layout="stacked"
                  />
                </Group>
              )}
            </Group>
          )}

          {currentTab === CruiseSectionTabValues.FINE_PRINT && (
            <CruiseFinePrint cruiseLine={offer.cruiseLine} compact />
          )}

          {currentTab === CruiseSectionTabValues.FAQ && hasFAQ && (
            <Group direction="vertical" gap={24}>
              <Heading variant="heading3">
                {sectionLabels[CruiseContentSections.FAQ]}
              </Heading>
              <CruiseFAQ
                ship={offer.ship}
                cruiseLine={offer.cruiseLine}
                cruiseType={offer.cruiseType}
                compact
              />
            </Group>
          )}
        </TabContent>
      </Group>
    </ContentWrapper>
  )
}

export default CruiseOfferDetailView
